import { useState } from "react";
import type { CurriculumType } from "@/react/components/Spaces/CourseSpace/AdminSummary/Engagement/types";
import { useCourseCustomLabels } from "@/react/components/Spaces/CourseSpace/Shared/useCourseCustomLabels";
import { drippedAtTippyContent } from "@/react/helpers/courseHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { LessonIcon } from "../../../Summary/Content/LessonIcon";

interface CurriculumProps {
  curriculum: CurriculumType;
}

export const Curriculum = ({ curriculum }: CurriculumProps) => {
  const { translatedLabels } = useCourseCustomLabels();
  const sectionIds = curriculum.map(section => section.id);
  const [expandedSections, setExpandedSections] =
    useState<number[]>(sectionIds);

  const isSectionExpanded = (sectionId: number) =>
    expandedSections.includes(sectionId);

  return (
    <div className="bg-primary border-primary mt-4 rounded-md border">
      <ul className="divide-primary list-none divide-y">
        {curriculum.map(section => (
          <li key={section.id}>
            <div className="bg-secondary w-full">
              <button
                type="button"
                className="focus-visible:outline-secondary flex w-full items-center px-4 py-2 focus-visible:rounded-md focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[-1px]"
                onClick={() => {
                  if (isSectionExpanded(section.id)) {
                    setExpandedSections(
                      expandedSections.filter(id => id !== section.id),
                    );
                  } else {
                    setExpandedSections([...expandedSections, section.id]);
                  }
                }}
              >
                <Icon
                  size={16}
                  type={
                    isSectionExpanded(section.id)
                      ? "16-chevron-right"
                      : "16-chevron-down"
                  }
                />
                <Typography.LabelXxs weight="semibold" truncate>
                  {section.name}
                </Typography.LabelXxs>
              </button>
            </div>

            {isSectionExpanded(section.id) && (
              <ul className="divide-primary list-none divide-y">
                {section.lessons.map(lesson => (
                  <li key={lesson.id}>
                    <TippyV2
                      content={drippedAtTippyContent({
                        drippedAt: section.dripped_at,
                        dripDelay: section.drip_delay,
                        needsToCompletePreviousLesson:
                          lesson.needs_to_complete_previous_lesson,
                        lessonLabelSingular: translatedLabels.lesson.singular,
                        lessonLabelPlural: translatedLabels.lesson.plural,
                        sectionLabelSingular: translatedLabels.section.singular,
                      })}
                      placement="bottom-start"
                      disabled={lesson.is_dripped}
                      arrow={false}
                      className="flex w-full items-center"
                      dataTestId="lesson-content-tittle"
                    >
                      <div className="flex w-full items-center px-4 py-2">
                        <LessonIcon
                          isDripped={lesson.is_dripped}
                          isSpaceMember
                          isCompleted={lesson.completed}
                        />
                        <div className="ml-3 flex-1 truncate">
                          <Typography.BodySm truncate>
                            {lesson.name}
                          </Typography.BodySm>
                        </div>
                      </div>
                    </TippyV2>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
